import { useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  getGroupMCPServerPoliciesSummary,
  resetGroupMCPServerPolicies,
} from '@kirin/api-client/api/mcp';
import {
  listGroupPolicyExceptions,
  resetGroupPolicyExceptions,
} from '@kirin/api-client/api/policy-exceptions';
import { queryKeys } from './queryKeys';
import {
  packageGroupRuleItem,
  skillGroupRuleItem,
  type InventoryGroupRuleItem,
} from './useInventoryGroupRuleActions';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-errors';
import { trackEvent } from '@/lib/analytics';

/**
 * KIR-4701: what "Reset to organization default" removes on one Inventory tab
 * — every Allow/Block the security group set there. Module constants, so the
 * hook keeps one identity across renders.
 */
export interface GroupScopeResetTarget {
  /** Plural, for the copy: "MCP servers", "skills", "packages". */
  noun: string;
  countQueryKey: (organizationId: string, groupId: string) => QueryKey;
  count: (organizationId: string, groupId: string) => Promise<number>;
  /** Resolves how many choices it removed. */
  resetAll: (organizationId: string, groupId: string) => Promise<number>;
  /** The table that shows the group's choices, refetched after a reset. */
  listQueryKey: (organizationId: string) => QueryKey;
  track: () => void;
}

export const mcpGroupScopeReset: GroupScopeResetTarget = {
  noun: 'MCP servers',
  countQueryKey: (organizationId, groupId) =>
    queryKeys.mcp.groupPoliciesSummary(organizationId, groupId),
  count: async (organizationId, groupId) =>
    (await getGroupMCPServerPoliciesSummary(apiClient, organizationId, groupId))
      .count,
  resetAll: async (organizationId, groupId) =>
    (await resetGroupMCPServerPolicies(apiClient, organizationId, groupId))
      .count,
  listQueryKey: () => queryKeys.mcp.all,
  track: () =>
    trackEvent('mcp_server_enforcement_change', {
      change: 'reset_all',
      scope: 'group',
    }),
};

/**
 * Skills and Packages keep a group's choices as policy-exception rules, one
 * per item; the reset deletes every rule the group has for the key in one
 * server transaction, so it never lands halfway.
 */
function groupRulesScopeReset<T>(
  item: InventoryGroupRuleItem<T>,
  noun: string,
  track: () => void
): GroupScopeResetTarget {
  const list = (organizationId: string, groupId: string) =>
    listGroupPolicyExceptions(
      apiClient,
      organizationId,
      groupId,
      item.policyKey
    );
  return {
    noun,
    // A leaf under the key the rule actions invalidate after every write, so
    // a row toggle refreshes the count.
    countQueryKey: (organizationId, groupId) => [
      ...queryKeys.policyExceptions.group(
        organizationId,
        groupId,
        item.policyKey
      ),
      'count',
    ],
    count: async (organizationId, groupId) =>
      (await list(organizationId, groupId)).exceptions.length,
    resetAll: async (organizationId, groupId) =>
      (
        await resetGroupPolicyExceptions(
          apiClient,
          organizationId,
          groupId,
          item.policyKey
        )
      ).count,
    listQueryKey: organizationId => item.listQueryKey(organizationId),
    track,
  };
}

export const skillsGroupScopeReset = groupRulesScopeReset(
  skillGroupRuleItem,
  'skills',
  () =>
    trackEvent('skill_list_change', {
      scope: 'group',
      source: 'skills_page',
      change: 'reset_all',
    })
);

export const packagesGroupScopeReset = groupRulesScopeReset(
  packageGroupRuleItem,
  'packages',
  () =>
    trackEvent('package_list_change', {
      scope: 'group',
      source: 'packages_page',
      change: 'reset_all',
    })
);

export interface GroupScopeReset {
  /** The group's own choices on the tab; `undefined` until loaded. */
  count: number | undefined;
  isCountLoading: boolean;
  /** The count could not be loaded; the reset is not offered. */
  isCountError: boolean;
  /** Resolves, never rejects: a failure is reported with a toast. */
  reset: () => Promise<void>;
  isResetting: boolean;
}

/**
 * KIR-4701: drop every Allow/Block a security group set on one Inventory tab,
 * so its members follow the organization's choices again. Idle without a
 * group or while `enabled` is false (a viewer who cannot edit).
 */
export function useGroupScopeReset(
  organizationId: string,
  securityGroupId: string | null,
  target: GroupScopeResetTarget,
  enabled: boolean
): GroupScopeReset {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const inFlight = useRef(false);
  const [isResetting, setIsResetting] = useState(false);

  const {
    data: count,
    isLoading: isCountLoading,
    isError: isCountError,
  } = useQuery({
    queryKey: target.countQueryKey(organizationId, securityGroupId ?? ''),
    queryFn: () => target.count(organizationId, securityGroupId ?? ''),
    enabled: enabled && securityGroupId !== null,
  });

  const reset = useCallback(async (): Promise<void> => {
    if (!securityGroupId || inFlight.current) return;
    inFlight.current = true;
    setIsResetting(true);
    try {
      // Only a reset that removed something is tracked, like a row write.
      if ((await target.resetAll(organizationId, securityGroupId)) > 0) {
        target.track();
      }
    } catch (error) {
      toast({
        title: 'Could not reset to the organization default',
        description: getApiErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      });
    } finally {
      // Refetch on failure too: another admin may have changed the group's
      // choices meanwhile, so the count shown may be stale.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: target.countQueryKey(organizationId, securityGroupId),
        }),
        queryClient.invalidateQueries({
          queryKey: target.listQueryKey(organizationId),
        }),
      ]).catch(() => undefined);
      inFlight.current = false;
      setIsResetting(false);
    }
  }, [organizationId, queryClient, securityGroupId, target, toast]);

  return { count, isCountLoading, isCountError, reset, isResetting };
}
